import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';

const SearchContext = createContext();

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within SearchProvider');
  }
  return context;
};

export const SearchProvider = ({ children }) => {
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [recentSearches, setRecentSearches] = useState(
    JSON.parse(localStorage.getItem('recentSearches') || '[]')
  );

  // Add the base URL here
  const API_URL = process.env.REACT_APP_API_URL || '';

  const search = useCallback(async (query, options = {}) => {
    setLoading(true);
    try {
      // Use the full URL with API_URL
      const response = await axios.get(`${API_URL}/api/search`, {
        params: { q: query, ...options }
      });
      
      setSearchResults(response.data);
      
      // Save to recent searches
      if (query && !recentSearches.includes(query)) {
        const updated = [query, ...recentSearches.slice(0, 9)];
        setRecentSearches(updated);
        localStorage.setItem('recentSearches', JSON.stringify(updated));
      }
      
      return response.data;
    } catch (error) {
      console.error('Search failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [recentSearches, API_URL]); // Add API_URL to dependencies

  const getAutocomplete = useCallback(async (prefix) => {
    if (!prefix || prefix.length < 2) {
      setSuggestions([]);
      return [];
    }

    try {
      // Use the full URL with API_URL
      const response = await axios.get(`${API_URL}/api/search/autocomplete`, {
        params: { q: prefix }
      });
      setSuggestions(response.data);
      return response.data;
    } catch (error) {
      console.error('Autocomplete failed:', error);
      return [];
    }
  }, [API_URL]); // Add API_URL to dependencies

  const clearRecentSearches = useCallback(() => {
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
  }, []);

  const value = {
    searchResults,
    loading,
    suggestions,
    recentSearches,
    search,
    getAutocomplete,
    clearRecentSearches
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
};